import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import './App.css';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Inventory from './pages/Inventory';
import ProductForm from './pages/ProductForm';
import AddProductWizard from './pages/AddProductWizard';
import Sale from './pages/Sale';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import Scanner from './pages/Scanner';
import { SCANNER_ENABLED } from './lib/features';

// Protected Route Component
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F5]">
        <div className="loader"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
}

// Public Route (redirect if authenticated)
function PublicRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F5]">
        <div className="loader"></div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } 
      />
      
      {/* Protected Routes */}
      <Route 
        path="/" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/inventory" 
        element={
          <ProtectedRoute>
            <Inventory />
          </ProtectedRoute>
        } 
      />
      {/* Adding walks one question at a time -- the shopkeeper chose this over
          the long form, and steps whose answers are already known skip
          themselves. Editing keeps the one-page form below: an edit exists to
          reach one field, and a wizard is the wrong tool for that. */}
      <Route 
        path="/inventory/add" 
        element={
          <ProtectedRoute>
            <AddProductWizard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/inventory/:id" 
        element={
          <ProtectedRoute>
            <ProductForm />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/sale" 
        element={
          <ProtectedRoute>
            <Sale />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/reports" 
        element={
          <ProtectedRoute>
            <Reports />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/settings" 
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        } 
      />
      {/* Not registered when the flag is off, so /scanner falls through to the
          catch-all below and lands on the dashboard rather than on a page whose
          only outcome is a 500. */}
      {SCANNER_ENABLED && (
      <Route
        path="/scanner"
        element={
          <ProtectedRoute>
            <Scanner />
          </ProtectedRoute>
        }
      />
      )}

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster 
            position="top-center"
            toastOptions={{
              style: {
                background: '#2D2D2D',
                color: '#FFFFFF',
                borderRadius: '12px',
              },
              duration: 3000,
            }}
          />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
